import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import {
  ALLOWED_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_OPTIONS,
  type Lang,
} from './localization'

// EN
const en = {
  common: {
    home: 'Home',
    library: 'Library',
    favorites: 'Favorites',
    settings: 'Settings',
    login: 'Login',
    logout: 'Logout',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    phone: 'Phone',
    city: 'City',
    businessType: 'Business Type',
    identifier: 'Identifier',
    play: 'Play',
    pause: 'Pause',
    next: 'Next',
    previous: 'Previous',
    addToFavorites: 'Add to Favorites',
    removeFromFavorites: 'Remove from Favorites',
    orders: 'Orders',
    likedMusic: 'Liked Music',
    likedPlaylists: 'Liked Playlists',
    appName: 'MusicTMM',
    a11y: {
      bottomNavigationShell: 'Bottom navigation shell',
      bottomNavigation: 'Bottom navigation',
    },
  },
  home: {
    welcomeMessage: 'Welcome to Music TMM',
    playlists: 'Playlists',
    moods: 'Moods',
    styles: 'Styles',
    businesses: 'Businesses',
    daytimes: 'Day Times',

    loading: 'Loading…',
    stats: {
      songs: 'songs',
      hours: 'hours',
      playlists: 'playlists',
    },
    actions: {
      more: 'More',
    },
    labels: {
      madeForYou: 'Made for you',
      category: 'Category',
    },
    a11y: {
      scrollLeft: 'scroll left',
      scrollRight: 'scroll right',
      scrollChipsLeft: 'scroll chips left',
      scrollChipsRight: 'scroll chips right',
    },
  },

  search: {
    placeholder: 'Search songs, albums, artists, podcasts',
    sections: {
      songs: 'Songs',
      playlists: 'Playlists',
    },
    states: {
      loading: 'Loading…',
      nothingFound: 'Nothing found',
      minChars: 'Type at least {{count}} letters',
    },
    errors: {
      search: 'Search error',
      unauthorized: '401 Unauthorized (token invalid for this instance)',
    },
  },

  orders: {
    title: 'Orders',
    searchPlaceholder: 'Search orders',
    createOrder: 'Create order',
    orderPlaylist: 'Order playlist',
    playlist: 'Playlist',
    pendingOrders: 'Pending orders',
    completedOrders: 'Completed orders',
    status: {
      processing: 'Processing your order',
      delivered: 'Order delivered',
    },
    notifications: {
      title: 'Orders',
    },
    errors: {
      failedToLoad: 'Failed to load orders',
    },
    a11y: {
      back: 'Back',
      menu: 'Menu',
    },
    create: {
      header: 'Order Playlist',
      businessInfo: 'Information about your business',
      fields: {
        login: 'LOGIN',
        activity: 'ACTIVITY',
        address: 'ADDRESS',
      },
      placeholders: {
        login: 'Login',
        activity: 'Activity',
        address: 'Address',
      },
      clientsInfo: 'Clients info',
      clients: {
        male: '% of male visitors',
        age18_30: '% of visitors aged 18 to 30',
        age31_45: '% of visitors aged 31 to 45',
        age46_60: '% of visitors aged 46 to 60',
        man: 'Man',
        woman: 'Woman',
      },
      styles: {
        title: 'Styles',
        options: {
          pop: 'POP',
          jazzBlues: 'Jazz/Blues',
          classical: 'Classical',
          rock: 'Rock',
          hiphopRnb: 'Hip-Hop/RnB',
          retro: 'Retro',
          clubDeepHouse: 'Club/Deep/House',
          funkSoul: 'Funk & Soul',
          loungeChillout: 'Lounge/Chill Out',
          bossaNova: 'Bossa Nova',
          latina: 'Latina',
          italian: 'Italian',
          france: 'France',
          ukrainian: 'Ukrainian',
          georgian: 'Georgian',
        },
      },
      ratios: {
        vocalInstrumental: 'VOCAL-INSTRUMENTAL RATIO',
        internationalLocal: 'INTERNATIONAL-LOCAL RATIO',
        vocal: 'Vocal',
        instrumental: 'Instrumental',
        international: "Int'l",
        local: 'Local',
      },
      linksLabel: 'LINKS TO MUSIC TRACK EXAMPLES',
      linksPlaceholder: 'Links to music track examples',
      commentaryTitle: 'Commentary',
      commentaryPlaceholder: 'Write your comment here',
      consent: 'I consent to the processing of personal data.',
      submit: 'Order',
      submitting: 'Ordering...',
      errors: {
        loginRequired: 'LOGIN is required',
        activityRequired: 'ACTIVITY is required',
        addressRequired: 'ADDRESS is required',
        selectAtLeastOneStyle: 'Select at least one style',
        consentRequired: 'Consent is required',
      },
    },
  },
  playlist: {
    songs: 'Songs',
    duration: 'Duration',
    updatedAt: 'Updated',
    playAll: 'Play All',

    // en.playlist
    timeShort: {
      minute: 'min',
      hour: 'h',
      day: 'd',
      month: 'mo',
      year: 'y',
    },
    actions: {
      back: 'Back',
      favorite: 'Favorite',
      like: 'Like',
    },

    meta: {
      songsCount_one: '{{count}} song',
      songsCount_other: '{{count}} songs',
      updated: 'Updated',
    },

    time: {
      justNow: 'just now',
      minute_one: '{{count}} minute',
      minute_other: '{{count}} minutes',
      hour_one: '{{count}} hour',
      hour_other: '{{count}} hours',
      day_one: '{{count}} day',
      day_other: '{{count}} days',
      month_one: '{{count}} month',
      month_other: '{{count}} months',
      year_one: '{{count}} year',
      year_other: '{{count}} years',
      ago: '{{value}} ago',
    },
  },

  // чтобы Settings-экран не падал в fallback на English-строки
  settings: {
    title: 'Settings',
    theme: 'Theme',
    changeTheme: 'Change Theme',
    changeThemeLabel: 'CHANGE THEME',
    language: 'Language',
    languageLabel: 'LANGUAGE',
    generalInfo: 'General Info',
    confidentialityAgreement: 'Confidentiality Agreement',
    accountInfo: 'Account Info',
    contacts: 'Contacts',

    accountInfoHint: 'Your personal data and verification status',
    basicInformation: 'Basic information',
    accountSecurity: 'Account & security',

    part1: 'Part 1',
    part2: 'Part 2',
    part3: 'Part 3',
    confidentialityText1:
      'In automatic mode, the app applies dark and light themes automatically according to your time zone.',
    confidentialityText2:
      'In automatic mode, the app applies dark and light themes automatically according to your time zone.',
    confidentialityText3:
      'In automatic mode, the app applies dark and light themes automatically according to your time zone.',

    // account info fields
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    identifier: 'Identifier',
    registeredAt: 'Registered at',
    validatedAt: 'Validated at',
    status: 'Status',
    statusVerified: 'Verified',
    statusNotVerified: 'Not verified',
    statusBanned: 'Banned',
    accountInfoLoading: 'Loading account info...',
    accountInfoUnavailable: 'Unable to load account info',

    // misc
    sendTo: 'Send to',
    marketingDepartment: 'Marketing Department',
    technicalDepartment: 'Technical Department',
    sendMessage: 'Send message',
    writeMessageHere: 'Write your message here',
    logout: 'Logout',
    back: 'Back',

    // theme values
    themeDark: 'Dark',
    themeLight: 'Light',
    themeDevice: 'Follow to device',
  },
}

// FR
const fr = {
  common: {
    home: 'Accueil',
    library: 'Bibliothèque',
    favorites: 'Favoris',
    settings: 'Paramètres',
    login: 'Connexion',
    logout: 'Déconnexion',
    register: "S'inscrire",
    email: 'Email',
    password: 'Mot de passe',
    name: 'Nom',
    phone: 'Téléphone',
    city: 'Ville',
    businessType: "Type d'entreprise",
    identifier: 'Identifiant',
    play: 'Jouer',
    pause: 'Pause',
    next: 'Suivant',
    previous: 'Précédent',
    addToFavorites: 'Ajouter aux favoris',
    removeFromFavorites: 'Retirer des favoris',
    // FR -> common
    orders: 'Commandes',
    likedMusic: 'Musiques aimées',
    likedPlaylists: 'Playlists aimées',
    appName: 'MusicTMM',
    a11y: {
      bottomNavigationShell: 'Conteneur de navigation inférieure',
      bottomNavigation: 'Navigation inférieure',
    },
  },
  home: {
    welcomeMessage: 'Bienvenue sur Music TMM',
    playlists: 'Listes de lecture',
    moods: 'Ambiances',
    styles: 'Styles',
    businesses: 'Entreprises',
    daytimes: 'Moments de la journée',

    loading: 'Chargement…',
    stats: {
      songs: 'morceaux',
      hours: 'heures',
      playlists: 'playlists',
    },
    actions: {
      more: 'Plus',
    },
    labels: {
      madeForYou: 'Créé pour vous',
      category: 'Catégorie',
    },
    a11y: {
      scrollLeft: 'défiler à gauche',
      scrollRight: 'défiler à droite',
      scrollChipsLeft: 'défiler les puces à gauche',
      scrollChipsRight: 'défiler les puces à droite',
    },
  },
  search: {
    placeholder: 'Rechercher des chansons, albums, artistes, podcasts',
    sections: {
      songs: 'Chansons',
      playlists: 'Playlists',
    },
    states: {
      loading: 'Chargement…',
      nothingFound: 'Aucun résultat',
      minChars: 'Tapez au moins {{count}} caractères',
    },
    errors: {
      search: 'Erreur de recherche',
      unauthorized: '401 Non autorisé (token invalide pour cette instance)',
    },
  },

  orders: {
    title: 'Commandes',
    searchPlaceholder: 'Rechercher des commandes',
    createOrder: 'Créer une commande',
    orderPlaylist: 'Commander une playlist',
    playlist: 'Playlist',
    pendingOrders: 'Commandes en attente',
    completedOrders: 'Commandes terminées',
    status: {
      processing: 'Traitement de votre commande',
      delivered: 'Commande livrée',
    },
    notifications: {
      title: 'Commandes',
    },
    errors: {
      failedToLoad: 'Échec du chargement des commandes',
    },
    a11y: {
      back: 'Retour',
      menu: 'Menu',
    },
    create: {
      header: 'Commander une playlist',
      businessInfo: 'Informations sur votre entreprise',
      fields: {
        login: 'IDENTIFIANT',
        activity: 'ACTIVITÉ',
        address: 'ADRESSE',
      },
      placeholders: {
        login: 'Identifiant',
        activity: 'Activité',
        address: 'Adresse',
      },
      clientsInfo: 'Infos clients',
      clients: {
        male: '% de visiteurs hommes',
        age18_30: '% des visiteurs de 18 à 30 ans',
        age31_45: '% des visiteurs de 31 à 45 ans',
        age46_60: '% des visiteurs de 46 à 60 ans',
        man: 'Homme',
        woman: 'Femme',
      },
      styles: {
        title: 'Styles',
        options: {
          pop: 'POP',
          jazzBlues: 'Jazz/Blues',
          classical: 'Classique',
          rock: 'Rock',
          hiphopRnb: 'Hip-Hop/RnB',
          retro: 'Rétro',
          clubDeepHouse: 'Club/Deep/House',
          funkSoul: 'Funk & Soul',
          loungeChillout: 'Lounge/Chill Out',
          bossaNova: 'Bossa Nova',
          latina: 'Latina',
          italian: 'Italien',
          france: 'Français',
          ukrainian: 'Ukrainien',
          georgian: 'Géorgien',
        },
      },
      ratios: {
        vocalInstrumental: 'RATIO VOCAL-INSTRUMENTAL',
        internationalLocal: 'RATIO INTERNATIONAL-LOCAL',
        vocal: 'Vocal',
        instrumental: 'Instrumental',
        international: 'Intl',
        local: 'Local',
      },
      linksLabel: 'LIENS VERS DES EXEMPLES DE MORCEAUX',
      linksPlaceholder: 'Liens vers des exemples de morceaux',
      commentaryTitle: 'Commentaire',
      commentaryPlaceholder: 'Écrivez votre commentaire ici',
      consent: "J'accepte le traitement des données personnelles.",
      submit: 'Commander',
      submitting: 'Commande...',
      errors: {
        loginRequired: "L'identifiant est requis",
        activityRequired: "L'activité est requise",
        addressRequired: "L'adresse est requise",
        selectAtLeastOneStyle: 'Choisissez au moins un style',
        consentRequired: 'Le consentement est requis',
      },
    },
  },
  playlist: {
    songs: 'Chansons',
    duration: 'Durée',
    updatedAt: 'Mis à jour',
    playAll: 'Tout jouer',

    // fr.playlist
    timeShort: {
      minute: 'min',
      hour: 'h',
      day: 'j',
      month: 'mois',
      year: 'an',
    },

    actions: {
      back: 'Retour',
      favorite: 'Favori',
      like: 'Aimer',
    },

    meta: {
      songsCount_one: '{{count}} chanson',
      songsCount_other: '{{count}} chansons',
      updated: 'Mis à jour',
    },

    time: {
      justNow: "à l'instant",
      minute_one: '{{count}} minute',
      minute_other: '{{count}} minutes',
      hour_one: '{{count}} heure',
      hour_other: '{{count}} heures',
      day_one: '{{count}} jour',
      day_other: '{{count}} jours',
      month_one: '{{count}} mois',
      month_other: '{{count}} mois',
      year_one: '{{count}} an',
      year_other: '{{count}} ans',
      ago: 'il y a {{value}}',
    },
  },

  settings: {
    title: 'Paramètres',
    theme: 'Thème',
    changeTheme: 'Changer le thème',
    changeThemeLabel: 'CHANGER LE THÈME',
    language: 'Langue',
    languageLabel: 'LANGUE',
    generalInfo: 'Informations générales',
    confidentialityAgreement: 'Accord de confidentialité',
    accountInfo: 'Informations du compte',
    contacts: 'Contacts',

    accountInfoHint: 'Vos données personnelles et votre statut de vérification',
    basicInformation: 'Informations de base',
    accountSecurity: 'Compte et sécurité',

    part1: 'Partie 1',
    part2: 'Partie 2',
    part3: 'Partie 3',
    confidentialityText1:
      "En mode automatique, l'application applique les thèmes sombre et clair automatiquement selon votre fuseau horaire.",
    confidentialityText2:
      "En mode automatique, l'application applique les thèmes sombre et clair automatiquement selon votre fuseau horaire.",
    confidentialityText3:
      "En mode automatique, l'application applique les thèmes sombre et clair automatiquement selon votre fuseau horaire.",

    // account info fields
    name: 'Nom',
    email: 'Email',
    phone: 'Téléphone',
    identifier: 'Identifiant',
    registeredAt: 'Inscrit le',
    validatedAt: 'Validé le',
    status: 'Statut',
    statusVerified: 'Vérifié',
    statusNotVerified: 'Non vérifié',
    statusBanned: 'Banni',
    accountInfoLoading: 'Chargement des informations du compte...',
    accountInfoUnavailable: 'Impossible de charger les informations du compte',

    // misc
    sendTo: 'Envoyer à',
    marketingDepartment: 'Département marketing',
    technicalDepartment: 'Département technique',
    sendMessage: 'Envoyer le message',
    writeMessageHere: 'Écrivez votre message ici',
    logout: 'Déconnexion',
    back: 'Retour',

    // theme values
    themeDark: 'Sombre',
    themeLight: 'Clair',
    themeDevice: "Suivre l'appareil",
  },
}

// UK
const uk = {
  common: {
    home: 'Головна',
    library: 'Бібліотека',
    favorites: 'Обране',
    settings: 'Налаштування',
    login: 'Увійти',
    logout: 'Вийти',
    register: 'Реєстрація',
    email: 'Email',
    password: 'Пароль',
    name: "Ім'я",
    phone: 'Телефон',
    city: 'Місто',
    businessType: 'Тип бізнесу',
    identifier: 'Ідентифікатор',
    play: 'Відтворити',
    pause: 'Пауза',
    next: 'Наступний',
    previous: 'Попередній',
    addToFavorites: 'Додати в обране',
    removeFromFavorites: 'Прибрати з обраного',
    orders: 'Замовлення',
    likedMusic: 'Уподобана музика',
    likedPlaylists: 'Уподобані плейлисти',
    appName: 'MusicTMM',
    a11y: {
      bottomNavigationShell: 'Контейнер нижньої навігації',
      bottomNavigation: 'Нижня навігація',
    },
  },
  home: {
    welcomeMessage: 'Ласкаво просимо до Music TMM',
    playlists: 'Плейлисти',
    moods: 'Настрої',
    styles: 'Стилі',
    businesses: 'Бізнеси',
    daytimes: 'Час доби',

    loading: 'Завантаження…',
    stats: {
      songs: 'пісень',
      hours: 'годин',
      playlists: 'плейлистів',
    },
    actions: {
      more: 'Ще',
    },
    labels: {
      madeForYou: 'Для вас',
      category: 'Категорія',
    },
    a11y: {
      scrollLeft: 'прокрутити ліворуч',
      scrollRight: 'прокрутити праворуч',
      scrollChipsLeft: 'прокрутити чіпи ліворуч',
      scrollChipsRight: 'прокрутити чіпи праворуч',
    },
  },
  search: {
    placeholder: 'Пошук пісень, альбомів, виконавців, подкастів',
    sections: {
      songs: 'Пісні',
      playlists: 'Плейлисти',
    },
    states: {
      loading: 'Завантаження…',
      nothingFound: 'Нічого не знайдено',
      minChars: 'Введіть щонайменше {{count}} символи',
    },
    errors: {
      search: 'Помилка пошуку',
      unauthorized: '401 Немає доступу (недійсний токен для цього середовища)',
    },
  },

  orders: {
    title: 'Замовлення',
    searchPlaceholder: 'Пошук замовлень',
    createOrder: 'Створити замовлення',
    orderPlaylist: 'Замовити плейлист',
    playlist: 'Плейлист',
    pendingOrders: 'Замовлення в обробці',
    completedOrders: 'Завершені замовлення',
    status: {
      processing: 'Ваше замовлення обробляється',
      delivered: 'Замовлення доставлено',
    },
    notifications: {
      title: 'Замовлення',
    },
    errors: {
      failedToLoad: 'Не вдалося завантажити замовлення',
    },
    a11y: {
      back: 'Назад',
      menu: 'Меню',
    },
    create: {
      header: 'Замовити плейлист',
      businessInfo: 'Інформація про ваш бізнес',
      fields: {
        login: 'ЛОГІН',
        activity: 'ДІЯЛЬНІСТЬ',
        address: 'АДРЕСА',
      },
      placeholders: {
        login: 'Логін',
        activity: 'Діяльність',
        address: 'Адреса',
      },
      clientsInfo: 'Інформація про клієнтів',
      clients: {
        male: '% чоловіків серед відвідувачів',
        age18_30: '% відвідувачів віком 18–30',
        age31_45: '% відвідувачів віком 31–45',
        age46_60: '% відвідувачів віком 46–60',
        man: 'Чоловік',
        woman: 'Жінка',
      },
      styles: {
        title: 'Стилі',
        options: {
          pop: 'POP',
          jazzBlues: 'Jazz/Blues',
          classical: 'Класика',
          rock: 'Rock',
          hiphopRnb: 'Hip-Hop/RnB',
          retro: 'Ретро',
          clubDeepHouse: 'Club/Deep/House',
          funkSoul: 'Funk & Soul',
          loungeChillout: 'Lounge/Chill Out',
          bossaNova: 'Bossa Nova',
          latina: 'Latina',
          italian: 'Італійський',
          france: 'Французький',
          ukrainian: 'Український',
          georgian: 'Грузинський',
        },
      },
      ratios: {
        vocalInstrumental: 'СПІВВІДНОШЕННЯ ВОКАЛ-ІНСТРУМЕНТАЛ',
        internationalLocal: 'СПІВВІДНОШЕННЯ МІЖНАРОДНА-ЛОКАЛЬНА',
        vocal: 'Вокал',
        instrumental: 'Інструментал',
        international: 'Int’l',
        local: 'Локальна',
      },
      linksLabel: 'ПОСИЛАННЯ НА ПРИКЛАДИ ТРЕКІВ',
      linksPlaceholder: 'Посилання на приклади треків',
      commentaryTitle: 'Коментар',
      commentaryPlaceholder: 'Напишіть ваш коментар тут',
      consent: 'Даю згоду на обробку персональних даних.',
      submit: 'Замовити',
      submitting: 'Замовлення...',
      errors: {
        loginRequired: 'ЛОГІН обовʼязковий',
        activityRequired: 'ДІЯЛЬНІСТЬ обовʼязкова',
        addressRequired: 'АДРЕСА обовʼязкова',
        selectAtLeastOneStyle: 'Оберіть хоча б один стиль',
        consentRequired: 'Потрібна згода',
      },
    },
  },
  playlist: {
    songs: 'Пісні',
    duration: 'Тривалість',
    updatedAt: 'Оновлено',
    playAll: 'Відтворити все',

    // uk.playlist
    timeShort: {
      minute: 'хв',
      hour: 'год',
      day: 'д',
      month: 'міс',
      year: 'р',
    },
    actions: {
      back: 'Назад',
      favorite: 'Уподобане',
      like: 'Подобається',
    },

    meta: {
      songsCount_one: '{{count}} пісня',
      songsCount_few: '{{count}} пісні',
      songsCount_many: '{{count}} пісень',
      songsCount_other: '{{count}} пісні',
      updated: 'Оновлено',
    },

    time: {
      justNow: 'щойно',
      minute_one: '{{count}} хвилину',
      minute_few: '{{count}} хвилини',
      minute_many: '{{count}} хвилин',
      minute_other: '{{count}} хвилини',

      hour_one: '{{count}} годину',
      hour_few: '{{count}} години',
      hour_many: '{{count}} годин',
      hour_other: '{{count}} години',

      day_one: '{{count}} день',
      day_few: '{{count}} дні',
      day_many: '{{count}} днів',
      day_other: '{{count}} дні',

      month_one: '{{count}} місяць',
      month_few: '{{count}} місяці',
      month_many: '{{count}} місяців',
      month_other: '{{count}} місяці',

      year_one: '{{count}} рік',
      year_few: '{{count}} роки',
      year_many: '{{count}} років',
      year_other: '{{count}} року',

      ago: '{{value}} тому',
    },
  },

  settings: {
    title: 'Налаштування',
    theme: 'Тема',
    changeTheme: 'Змінити тему',
    changeThemeLabel: 'ЗМІНИТИ ТЕМУ',
    language: 'Мова',
    languageLabel: 'МОВА',
    generalInfo: 'Загальна інформація',
    confidentialityAgreement: 'Угода про конфіденційність',
    accountInfo: 'Інформація акаунта',
    contacts: 'Контакти',

    accountInfoHint: 'Ваші персональні дані та статус верифікації',
    basicInformation: 'Основна інформація',
    accountSecurity: 'Акаунт і безпека',

    part1: 'Частина 1',
    part2: 'Частина 2',
    part3: 'Частина 3',
    confidentialityText1:
      'У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються відповідно до Вашого часового поясу.',
    confidentialityText2:
      'У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються відповідно до Вашого часового поясу.',
    confidentialityText3:
      'У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються відповідно до Вашого часового поясу.',

    // account info fields
    name: "Ім'я",
    email: 'Email',
    phone: 'Телефон',
    identifier: 'Ідентифікатор',
    registeredAt: 'Зареєстровано',
    validatedAt: 'Підтверджено',
    status: 'Статус',
    statusVerified: 'Підтверджено',
    statusNotVerified: 'Не підтверджено',
    statusBanned: 'Заблоковано',
    accountInfoLoading: 'Завантаження даних акаунта...',
    accountInfoUnavailable: 'Не вдалося завантажити дані акаунта',

    // misc
    sendTo: 'Надіслати до',
    marketingDepartment: 'Маркетинговий відділ',
    technicalDepartment: 'Технічний відділ',
    sendMessage: 'Надіслати повідомлення',
    writeMessageHere: 'Напишіть ваше повідомлення тут',
    logout: 'Вийти',
    back: 'Назад',

    // theme values
    themeDark: 'Темна',
    themeLight: 'Світла',
    themeDevice: 'Як на пристрої',
  },
}

const ALL_RESOURCES: Record<Lang, any> = { en, fr, uk }

const resources = ALLOWED_LANGUAGES.reduce(
  (acc, lng) => {
    acc[lng] = ALL_RESOURCES[lng]
    return acc
  },
  {} as Record<Lang, any>,
)

const LANG_STORAGE_KEY = 'i18nextLng'

function normalizeLang(raw?: string | null): Lang | null {
  const code = String(raw || '').split('-')[0] as Lang
  return ALLOWED_LANGUAGES.includes(code) ? code : null
}

function getInitialLang(): Lang {
  // SSR-safe
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE

  try {
    const saved = normalizeLang(window.localStorage.getItem(LANG_STORAGE_KEY))
    if (saved) return saved
  } catch {}

  const browser = normalizeLang(window.navigator?.language ?? null)
  return browser || DEFAULT_LANGUAGE
}

void i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLang(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: ALLOWED_LANGUAGES,
  ns: ['common', 'home', 'playlist', 'settings', 'search', 'orders'],
  defaultNS: 'common',
  interpolation: { escapeValue: false },
})

i18n.on('languageChanged', (lng) => {
  const safe = normalizeLang(lng) || DEFAULT_LANGUAGE

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, safe)
    } catch {}
  }

  if (safe !== lng) void i18n.changeLanguage(safe)
})

export { LANGUAGE_OPTIONS, ALLOWED_LANGUAGES, DEFAULT_LANGUAGE }
export type { Lang }
export default i18n
